export enum DataStorageKeys {
  AUTH = "auth",
}

export interface AuthTokensStorage {
  accessToken: string;
  refreshToken: string;
}

/**
 * DataStorageService - A client-side data storage service using localStorage
 *
 * This service provides CRUD operations for storing and retrieving data
 * in the browser's localStorage with automatic JSON serialization/deserialization.
 */
export class DataStorageService {
  /**
   * Creates the full storage key with prefix
   * @param key - The base key
   * @returns The prefixed key
   */
  private getFullKey(key: string): string {
    return key;
  }

  /**
   * Creates or updates an item in localStorage
   * @param key - The storage key
   * @param data - The data to store (will be JSON stringified)
   * @returns True if successful, false otherwise
   */
  create<T>(key: DataStorageKeys, data: T): boolean {
    try {
      const fullKey = this.getFullKey(key);
      const serializedData = JSON.stringify(data);

      localStorage.setItem(fullKey, serializedData);

      return true;
    } catch (error) {
      console.error("Failed to create item in localStorage:", error);

      return false;
    }
  }

  /**
   * Retrieves an item from localStorage
   * @param key - The storage key
   * @returns The parsed data or null if not found
   */
  read<T>(key: DataStorageKeys): T | null {
    try {
      const fullKey = this.getFullKey(key);
      const item = localStorage.getItem(fullKey);

      if (item === null) {
        return null;
      }

      return JSON.parse(item) as T;
    } catch (error) {
      console.error("Failed to read item from localStorage:", error);

      return null;
    }
  }

  /**
   * Updates an existing item in localStorage
   * @param key - The storage key
   * @param data - The new data to store
   * @returns True if successful, false otherwise
   */
  update<T>(key: DataStorageKeys, data: T): boolean {
    try {
      const fullKey = this.getFullKey(key);

      // Check if item exists
      if (!localStorage.getItem(fullKey)) {
        console.warn(`Item with key "${key}" does not exist`);

        return false;
      }

      const serializedData = JSON.stringify(data);

      localStorage.setItem(fullKey, serializedData);

      return true;
    } catch (error) {
      console.error("Failed to update item in localStorage:", error);

      return false;
    }
  }

  /**
   * Deletes an item from localStorage
   * @param key - The storage key
   * @returns True if successful, false otherwise
   */
  delete(key: DataStorageKeys): boolean {
    try {
      const fullKey = this.getFullKey(key);

      localStorage.removeItem(fullKey);

      return true;
    } catch (error) {
      console.error("Failed to delete item from localStorage:", error);

      return false;
    }
  }

  /**
   * Checks if an item exists in localStorage
   * @param key - The storage key
   * @returns True if the item exists, false otherwise
   */
  exists(key: DataStorageKeys): boolean {
    const fullKey = this.getFullKey(key);

    return localStorage.getItem(fullKey) !== null;
  }

  /**
   * Gets all keys that match the service's prefix
   * @returns Array of keys (without prefix)
   */
  getAllKeys(): DataStorageKeys[] {
    const keys: DataStorageKeys[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key) {
        keys.push(key as DataStorageKeys);
      }
    }

    return keys;
  }

  /**
   * Clears all items with the service's prefix
   * @returns True if successful, false otherwise
   */
  clear(): boolean {
    try {
      const keys = this.getAllKeys();

      keys.forEach((key) => this.delete(key));

      return true;
    } catch (error) {
      console.error("Failed to clear localStorage items:", error);

      return false;
    }
  }

  /**
   * Gets the storage size in bytes for items with the service's prefix
   * @returns Size in bytes
   */
  getStorageSize(): number {
    let size = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (key) {
        const value = localStorage.getItem(key);

        if (value) {
          size += key.length + value.length;
        }
      }
    }

    return size;
  }

  // Auth tokens management

  hasAuthTokens(): boolean {
    return this.exists(DataStorageKeys.AUTH);
  }

  getAuthTokens(): AuthTokensStorage | null {
    return this.read(DataStorageKeys.AUTH);
  }

  putAuthTokens(tokens: AuthTokensStorage): void {
    this.create(DataStorageKeys.AUTH, tokens);
  }

  clearAuthTokens(): void {
    this.delete(DataStorageKeys.AUTH);
  }
}

// Export a default instance for convenience
export const dataStorage = new DataStorageService();
